import { useEffect, useMemo, useState } from 'react'
import { CalendarCheck, Clock, X } from 'lucide-react'
import type { Appointment, AvailabilitySlot } from '@/services/appointmentsApi'
import { appointmentsApi } from '@/services/appointmentsApi'
import { useAuthStore } from '@/store/authStore'
import { useVerticalUI } from '@/lib/verticalUI'

interface ConfirmAppointmentModalProps {
  appointment: Appointment
  onClose: () => void
  onConfirmed: () => void
}

function toLocalInput(iso: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${iso.getFullYear()}-${pad(iso.getMonth() + 1)}-${pad(iso.getDate())}T${pad(iso.getHours())}:${pad(iso.getMinutes())}`
}

/**
 * Modal de confirmación MANUAL de cita (fase A3).
 * El slot definitivo lo fija el personal; el paciente recibe la plantilla
 * de confirmación por WhatsApp al confirmar (invariante #5 del plan).
 */
export default function ConfirmAppointmentModal({
  appointment,
  onClose,
  onConfirmed,
}: ConfirmAppointmentModalProps) {
  const { profile } = useVerticalUI()
  const currentUser = useAuthStore((s) => s.user)

  const suggestedStart = useMemo(() => {
    const base = appointment.requestedSlotStart
      ? new Date(appointment.requestedSlotStart)
      : new Date(Date.now() + 24 * 60 * 60 * 1000)
    base.setSeconds(0, 0)
    return base
  }, [appointment.requestedSlotStart])

  const [startAt, setStartAt] = useState<string>(toLocalInput(suggestedStart))
  const [minutes, setMinutes] = useState<number>(30)
  const [staffUserId] = useState<string>(currentUser?.id ?? '')
  const [locationOrRoom, setLocationOrRoom] = useState<string>(
    appointment.modality === 'REMOTE' ? 'Videoconsulta (enlace por WhatsApp)' : ''
  )
  const [customMessage, setCustomMessage] = useState<string>('')
  const [availableSlots, setAvailableSlots] = useState<AvailabilitySlot[]>([])
  const [dayEnabled, setDayEnabled] = useState<boolean>(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fase A7: disponibilidad del profesional para el día seleccionado (slots
  // libres según BusinessHours del tenant menos las citas CONFIRMED).
  useEffect(() => {
    if (!currentUser?.id || !startAt) return
    const date = startAt.slice(0, 10)
    appointmentsApi
      .getStaffAvailability(currentUser.id, date)
      .then((result) => {
        setAvailableSlots(result.slots ?? [])
        setDayEnabled(result.dayEnabled)
      })
      .catch(() => {
        setAvailableSlots([])
        setDayEnabled(true)
      })
  }, [currentUser?.id, startAt])

  const suggestedSlots = useMemo(
    () => availableSlots.filter((slot) => slot.available).slice(0, 8),
    [availableSlots]
  )

  useEffect(() => {
    const name = appointment.patientName || profile.terminology.customerRoleLabel.toLowerCase()
    const service = appointment.serviceName || profile.terminology.itemSingularName.toLowerCase()
    const when = new Date(startAt)
    const date = when.toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' })
    const time = when.toLocaleTimeString('es-VE', { hour: 'numeric', minute: '2-digit', hour12: true })
    setCustomMessage(
      `¡Listo, ${name}! Tu ${profile.terminology.primaryTransactionSingular.toLowerCase()} de ${service} quedó confirmada para el ${date} a las ${time}${
        locationOrRoom.trim() ? ` en ${locationOrRoom.trim()}` : ''
      }. ¡Te esperamos!`
    )
    // Solo recalculamos la vista previa si el usuario no la editó manualmente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt, locationOrRoom, profile])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const start = new Date(startAt)
    if (Number.isNaN(start.getTime())) {
      setError('Fecha de inicio inválida')
      return
    }
    const end = new Date(start.getTime() + minutes * 60 * 1000)
    if (!staffUserId) {
      setError('Selecciona el profesional que atenderá la cita')
      return
    }
    setSaving(true)
    try {
      await appointmentsApi.confirmAppointment(appointment.id, {
        confirmedSlotStart: start.toISOString(),
        confirmedSlotEnd: end.toISOString(),
        staffUserId,
        locationOrRoom: locationOrRoom.trim() || undefined,
        customMessage: customMessage.trim() || undefined,
      })
      onConfirmed()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo confirmar la cita')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-800">
              Confirmar {profile.terminology.primaryTransactionSingular.toLowerCase()} manualmente
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <p className="font-medium text-slate-700">{appointment.patientName ?? profile.terminology.customerRoleLabel}</p>
            <p>
              {appointment.serviceName ?? profile.terminology.itemSingularName} ·{' '}
              {appointment.modality === 'REMOTE' ? 'Telemedicina 💻' : 'Presencial 🏥'}
            </p>
            <p className="text-xs text-slate-500">Pidió: “{appointment.requestedSlotText}”</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Inicio</span>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-emerald-500 focus:outline-none"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Duración</span>
              <select
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-emerald-500 focus:outline-none"
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={45}>45 minutos</option>
                <option value={60}>60 minutos</option>
              </select>
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">{profile.terminology.staffRoleLabel}</span>
            <input
              type="text"
              value={currentUser?.name ?? staffUserId}
              readOnly
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600"
            />
            <input type="hidden" value={staffUserId} />
          </label>

          {!dayEnabled && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
              El negocio no atiende el día seleccionado según sus horarios. Ajusta la fecha o continúa bajo tu criterio.
            </p>
          )}

          {suggestedSlots.length > 0 && (
            <div className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium text-slate-700">
                <Clock className="h-3.5 w-3.5" />
                Horas libres del profesional (clic para elegir)
              </span>
              <div className="flex flex-wrap gap-1.5">
                {suggestedSlots.map((slot) => {
                  const slotTime = new Date(slot.start)
                  const isSelected =
                    startAt ===
                    `${slotTime.getFullYear()}-${String(slotTime.getMonth() + 1).padStart(2, '0')}-${String(slotTime.getDate()).padStart(2, '0')}T${String(slotTime.getHours()).padStart(2, '0')}:${String(slotTime.getMinutes()).padStart(2, '0')}`
                  return (
                    <button
                      key={slot.start}
                      type="button"
                      onClick={() => {
                        const d = new Date(slot.start)
                        setStartAt(toLocalInput(d))
                      }}
                      className={[
                        'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                        isSelected
                          ? 'border-emerald-500 bg-emerald-600 text-white'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
                      ].join(' ')}
                    >
                      {slotTime.toLocaleTimeString('es-VE', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">{profile.terminology.locationLabel}</span>
            <input
              type="text"
              value={locationOrRoom}
              onChange={(e) => setLocationOrRoom(e.target.value)}
              placeholder={profile.terminology.locationLabel}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-emerald-500 focus:outline-none"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">
              Mensaje WhatsApp al {profile.terminology.customerRoleLabel.toLowerCase()} (editable)
            </span>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-emerald-500 focus:outline-none"
            />
          </label>

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? 'Confirmando…' : 'Confirmar y enviar notificación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
